import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { auditLogKeys } from '@/features/audit-log';

import {
  adjustCustomerPoints,
  createLoyaltyBrandRule,
  getCustomerPoints,
  getCustomerPointsHistory,
  getLoyaltyBrandRules,
  getLoyaltyRules,
  saveLoyaltyRules,
  setLoyaltyBrandRuleActive,
  updateLoyaltyBrandRule,
} from './loyalty.api';
import type {
  CustomerPointsHistoryParams,
  LoyaltyBrandRuleListParams,
  LoyaltyRulesInput,
  PointsAdjustmentInput,
} from '../types/loyalty.types';

export const loyaltyKeys = {
  all: ['loyalty'] as const,
  rules: (countryId: number) => [...loyaltyKeys.all, 'rules', countryId] as const,
  brandRules: () => [...loyaltyKeys.all, 'brand-rules'] as const,
  brandRuleList: (countryId: number, params: LoyaltyBrandRuleListParams) =>
    [...loyaltyKeys.brandRules(), countryId, params] as const,
  customer: (customerId: number) => [...loyaltyKeys.all, 'customer', customerId] as const,
  customerBalance: (customerId: number) =>
    [...loyaltyKeys.customer(customerId), 'balance'] as const,
  customerHistory: (customerId: number, params: CustomerPointsHistoryParams) =>
    [...loyaltyKeys.customer(customerId), 'history', params] as const,
};

export function useLoyaltyRules(countryId: number | undefined) {
  return useQuery({
    queryKey: loyaltyKeys.rules(countryId ?? 0),
    queryFn: ({ signal }) => getLoyaltyRules(countryId!, signal),
    enabled: countryId !== undefined,
  });
}

export function useSaveLoyaltyRules(countryId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoyaltyRulesInput) => saveLoyaltyRules(countryId, input),
    onSuccess: (result) => {
      toast.success(result.changed > 0 ? 'Earning rules saved.' : 'Nothing changed.');
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.rules(countryId) });
      void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
    },
  });
}

export function useLoyaltyBrandRules(
  countryId: number | undefined,
  params: LoyaltyBrandRuleListParams,
) {
  return useQuery({
    queryKey: loyaltyKeys.brandRuleList(countryId ?? 0, params),
    queryFn: ({ signal }) => getLoyaltyBrandRules(countryId!, params, signal),
    enabled: countryId !== undefined,
    placeholderData: (previous) => previous,
  });
}

function useInvalidateBrandRules() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: loyaltyKeys.brandRules() });
    void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
  };
}

export function useCreateLoyaltyBrandRule(countryId: number) {
  const invalidate = useInvalidateBrandRules();
  return useMutation({
    mutationFn: (input: { brandId: number; points: number }) =>
      createLoyaltyBrandRule(countryId, input),
    onSuccess: () => {
      toast.success('Brand rule added.');
      invalidate();
    },
  });
}

export function useUpdateLoyaltyBrandRule() {
  const invalidate = useInvalidateBrandRules();
  return useMutation({
    mutationFn: (input: { id: number; points: number }) =>
      updateLoyaltyBrandRule(input.id, input.points),
    onSuccess: () => {
      toast.success('Brand rule updated.');
      invalidate();
    },
  });
}

export function useSetLoyaltyBrandRuleActive() {
  const invalidate = useInvalidateBrandRules();
  return useMutation({
    mutationFn: (input: { id: number; active: boolean }) =>
      setLoyaltyBrandRuleActive(input.id, input.active),
    onSuccess: (_, input) => {
      toast.success(input.active ? 'Brand rule activated.' : 'Brand rule deactivated.');
      invalidate();
    },
  });
}

export function useCustomerPoints(customerId: number) {
  return useQuery({
    queryKey: loyaltyKeys.customerBalance(customerId),
    queryFn: ({ signal }) => getCustomerPoints(customerId, signal),
  });
}

export function useCustomerPointsHistory(customerId: number, params: CustomerPointsHistoryParams) {
  return useQuery({
    queryKey: loyaltyKeys.customerHistory(customerId, params),
    queryFn: ({ signal }) => getCustomerPointsHistory(customerId, params, signal),
    placeholderData: (previous) => previous,
  });
}

export function useAdjustCustomerPoints(customerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PointsAdjustmentInput) => adjustCustomerPoints(customerId, input),
    onSuccess: (result, input) => {
      toast.success(
        !result.applied
          ? 'This adjustment was already applied.'
          : input.direction === 'add'
            ? `Added ${input.points} points.`
            : `Deducted ${input.points} points.`,
      );
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customer(customerId) });
      void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
    },
  });
}
